/**
 * Anchor Bullion Investing Guide JavaScript
 * Namespace: ab-investing
 */

(function() {
  'use strict';

  // Module configuration
  const config = {
    itemsPerLoad: 6,
    debounceDelay: 300,
    storageKey: 'ab-investing-filters',
    storageVersion: '1.0'
  };

  // State management
  const state = {
    articles: [],
    filteredArticles: [],
    displayedCount: 0,
    filters: {
      search: '',
      topic: [],
      metal: [],
      level: [],
      format: []
    },
    sort: 'helpful'
  };

  /**
   * Initialize the module
   */
  function init() {
    // Bail early if not on the investing guide page
    const container = document.querySelector('.ab-investing-guide');
    if (!container) return;

    // Cache DOM elements
    const elements = {
      container,
      searchInput: document.getElementById('ab-guide-search'),
      filterToggles: document.querySelectorAll('.ab-filter-toggle'),
      filterInputs: document.querySelectorAll('[data-filter]'),
      sortSelect: document.getElementById('ab-sort-select'),
      articlesGrid: document.getElementById('ab-articles-grid'),
      loadMoreBtn: document.getElementById('ab-load-more'),
      resultsCount: document.getElementById('ab-results-count')
    };

    // Initialize articles from DOM
    initializeArticles(elements.articlesGrid);
    
    // Restore saved filters
    restoreFilters();
    
    // Set up event listeners
    setupEventListeners(elements);
    
    // Initial render
    filterAndSort();
    updateDisplay(elements);
  }

  /**
   * Initialize articles from DOM
   */
  function initializeArticles(grid) {
    if (!grid) return;
    
    const cards = grid.querySelectorAll('.ab-article-card');
    state.articles = Array.from(cards).map(card => ({
      element: card,
      title: card.querySelector('.ab-article-card__title a')?.textContent || '',
      summary: card.querySelector('.ab-article-card__summary')?.textContent || '',
      topic: card.dataset.topic || '',
      metal: card.dataset.metal || '',
      level: card.dataset.level || '',
      format: card.dataset.format || '',
      helpful: parseInt(card.dataset.helpful) || 0,
      views: parseInt(card.dataset.views) || 0,
      updated: card.dataset.updated || '',
      time: parseInt(card.dataset.time) || 0
    }));
    
    state.displayedCount = Math.min(config.itemsPerLoad, state.articles.length);
  }

  /**
   * Set up event listeners
   */
  function setupEventListeners(elements) {
    // Search input
    if (elements.searchInput) {
      let searchTimeout;
      elements.searchInput.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          state.filters.search = e.target.value.toLowerCase();
          filterAndSort();
          updateDisplay(elements);
          saveFilters();
        }, config.debounceDelay);
      });
      
      // Handle Enter key
      elements.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          clearTimeout(searchTimeout);
          state.filters.search = e.target.value.toLowerCase();
          filterAndSort();
          updateDisplay(elements);
          saveFilters();
        }
      });
    }

    // Filter toggles
    elements.filterToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        const targetId = toggle.getAttribute('aria-controls');
        const target = document.getElementById(targetId);
        
        // Close other dropdowns
        elements.filterToggles.forEach(otherToggle => {
          if (otherToggle !== toggle) {
            otherToggle.setAttribute('aria-expanded', 'false');
            const otherId = otherToggle.getAttribute('aria-controls');
            const otherTarget = document.getElementById(otherId);
            if (otherTarget) otherTarget.hidden = true;
          }
        });
        
        // Toggle current dropdown
        toggle.setAttribute('aria-expanded', !expanded);
        if (target) target.hidden = expanded;
      });
    });

    // Filter checkboxes
    elements.filterInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const filterType = input.dataset.filter;
        const value = input.value;
        
        if (input.checked) {
          if (!state.filters[filterType].includes(value)) {
            state.filters[filterType].push(value);
          }
        } else {
          const index = state.filters[filterType].indexOf(value);
          if (index > -1) {
            state.filters[filterType].splice(index, 1);
          }
        }
        
        filterAndSort();
        updateDisplay(elements);
        saveFilters();
      });
    });

    // Sort select
    if (elements.sortSelect) {
      elements.sortSelect.addEventListener('change', (e) => {
        state.sort = e.target.value;
        filterAndSort();
        updateDisplay(elements);
        saveFilters();
      });
    }

    // Load more button
    if (elements.loadMoreBtn) {
      elements.loadMoreBtn.addEventListener('click', () => {
        state.displayedCount = Math.min(
          state.displayedCount + config.itemsPerLoad,
          state.filteredArticles.length
        );
        updateDisplay(elements);
      });
    }

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.ab-filter-group')) {
        elements.filterToggles.forEach(toggle => {
          toggle.setAttribute('aria-expanded', 'false');
          const targetId = toggle.getAttribute('aria-controls');
          const target = document.getElementById(targetId);
          if (target) target.hidden = true;
        });
      }
    });

    // Keyboard navigation for dropdowns
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        elements.filterToggles.forEach(toggle => {
          toggle.setAttribute('aria-expanded', 'false');
          const targetId = toggle.getAttribute('aria-controls');
          const target = document.getElementById(targetId);
          if (target) target.hidden = true;
        });
      }
    });
  }

  /**
   * Filter and sort articles
   */
  function filterAndSort() {
    // Filter articles
    state.filteredArticles = state.articles.filter(article => {
      // Search filter
      if (state.filters.search) {
        const searchTerm = state.filters.search;
        const matchesSearch = 
          article.title.toLowerCase().includes(searchTerm) ||
          article.summary.toLowerCase().includes(searchTerm);
        if (!matchesSearch) return false;
      }
      
      // Topic filter
      if (state.filters.topic.length > 0) {
        if (!state.filters.topic.includes(article.topic)) return false;
      }
      
      // Metal filter
      if (state.filters.metal.length > 0) {
        if (!state.filters.metal.includes(article.metal)) return false;
      }
      
      // Level filter
      if (state.filters.level.length > 0) {
        if (!state.filters.level.includes(article.level)) return false;
      }
      
      // Format filter
      if (state.filters.format.length > 0) {
        if (!state.filters.format.includes(article.format)) return false;
      }
      
      return true;
    });
    
    // Sort articles
    state.filteredArticles.sort((a, b) => {
      switch (state.sort) {
        case 'helpful':
          return b.helpful - a.helpful;
        case 'newest':
          return new Date(b.updated) - new Date(a.updated);
        case 'views':
          return b.views - a.views;
        case 'time':
          return a.time - b.time;
        default:
          return 0;
      }
    });
    
    // Reset displayed count
    state.displayedCount = Math.min(config.itemsPerLoad, state.filteredArticles.length);
  }

  /**
   * Update the display
   */
  function updateDisplay(elements) {
    if (!elements.articlesGrid) return;
    
    // Hide all articles first
    state.articles.forEach(article => {
      article.element.style.display = 'none';
      article.element.setAttribute('aria-hidden', 'true');
    });
    
    // Show filtered articles up to displayed count
    state.filteredArticles.slice(0, state.displayedCount).forEach(article => {
      article.element.style.display = '';
      article.element.setAttribute('aria-hidden', 'false');
    });
    
    // Update results count
    if (elements.resultsCount) {
      const totalCount = state.filteredArticles.length;
      const showingCount = Math.min(state.displayedCount, totalCount);
      
      let countText = '';
      if (state.filters.search || hasActiveFilters()) {
        countText = `Showing ${showingCount} of ${totalCount} matching articles`;
      } else {
        countText = `Showing ${showingCount} of ${totalCount} articles`;
      }
      
      elements.resultsCount.textContent = countText;
      elements.resultsCount.setAttribute('aria-live', 'polite');
    }
    
    // Update load more button
    if (elements.loadMoreBtn) {
      if (state.displayedCount >= state.filteredArticles.length) {
        elements.loadMoreBtn.style.display = 'none';
      } else {
        elements.loadMoreBtn.style.display = '';
        const remaining = state.filteredArticles.length - state.displayedCount;
        elements.loadMoreBtn.textContent = `Load More Guides (${remaining} remaining)`;
      }
    }
  }

  /**
   * Check if any filters are active
   */
  function hasActiveFilters() {
    return state.filters.topic.length > 0 ||
           state.filters.metal.length > 0 ||
           state.filters.level.length > 0 ||
           state.filters.format.length > 0;
  }

  /**
   * Save filters to localStorage
   */
  function saveFilters() {
    try {
      const data = {
        version: config.storageVersion,
        filters: state.filters,
        sort: state.sort
      };
      localStorage.setItem(config.storageKey, JSON.stringify(data));
    } catch (e) {
      console.log('Could not save filters:', e);
    }
  }

  /**
   * Restore filters from localStorage
   */
  function restoreFilters() {
    try {
      const stored = localStorage.getItem(config.storageKey);
      if (!stored) return;
      
      const data = JSON.parse(stored);
      if (data.version !== config.storageVersion) {
        localStorage.removeItem(config.storageKey);
        return;
      }
      
      // Restore filters
      if (data.filters) {
        state.filters = { ...state.filters, ...data.filters };
        
        // Update search input
        const searchInput = document.getElementById('ab-guide-search');
        if (searchInput && state.filters.search) {
          searchInput.value = state.filters.search;
        }
        
        // Update checkboxes
        document.querySelectorAll('[data-filter]').forEach(input => {
          const filterType = input.dataset.filter;
          const value = input.value;
          input.checked = state.filters[filterType].includes(value);
        });
      }
      
      // Restore sort
      if (data.sort) {
        state.sort = data.sort;
        const sortSelect = document.getElementById('ab-sort-select');
        if (sortSelect) {
          sortSelect.value = state.sort;
        }
      }
    } catch (e) {
      console.log('Could not restore filters:', e);
    }
  }

  /**
   * Handle URL parameters (optional enhancement)
   */
  function handleUrlParams() {
    const params = new URLSearchParams(window.location.search);
    
    // Search parameter
    const search = params.get('search');
    if (search) {
      state.filters.search = search.toLowerCase();
      const searchInput = document.getElementById('ab-guide-search');
      if (searchInput) searchInput.value = search;
    }
    
    // Filter parameters
    ['topic', 'metal', 'level', 'format'].forEach(filterType => {
      const values = params.getAll(filterType);
      if (values.length > 0) {
        state.filters[filterType] = values;
        document.querySelectorAll(`[data-filter="${filterType}"]`).forEach(input => {
          input.checked = values.includes(input.value);
        });
      }
    });
    
    // Sort parameter
    const sort = params.get('sort');
    if (sort && ['helpful', 'newest', 'views', 'time'].includes(sort)) {
      state.sort = sort;
      const sortSelect = document.getElementById('ab-sort-select');
      if (sortSelect) sortSelect.value = sort;
    }
  }

  /**
   * Update URL parameters (optional enhancement)
   */
  function updateUrlParams() {
    const params = new URLSearchParams();
    
    // Add search
    if (state.filters.search) {
      params.set('search', state.filters.search);
    }
    
    // Add filters
    ['topic', 'metal', 'level', 'format'].forEach(filterType => {
      state.filters[filterType].forEach(value => {
        params.append(filterType, value);
      });
    });
    
    // Add sort
    if (state.sort !== 'helpful') {
      params.set('sort', state.sort);
    }
    
    // Update URL without reload
    const newUrl = params.toString() 
      ? `${window.location.pathname}?${params.toString()}` 
      : window.location.pathname;
    
    window.history.replaceState({}, '', newUrl);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Export for debugging (optional)
  window.abInvestingGuide = {
    state,
    config,
    filterAndSort,
    updateDisplay
  };
})();